import axios from 'axios'
import qs from 'qs'
import {ElMessage} from 'element-plus'
import store from '@/store'
import cache from '@/utils/cache'

// axios实例
const service = axios.create({
    baseURL: import.meta.env.VITE_API_URL as any,
    timeout: 60000,
    headers: {'Content-Type': 'application/json;charset=UTF-8'}
})

// 请求拦截器
service.interceptors.request.use(
    (config: any) => {
        const userStore = store.userStore
        if (userStore?.token) {
            config.headers.Authorization = userStore.token
        }

        config.headers['Accept-Language'] = cache.getLanguage()

        if (!config.headers['Authorization']) {
            let token = cache.getToken();
            if (token) {
                config.headers['Authorization'] = token;
            }
        }

        // 追加时间戳，防止GET请求缓存
        if (config.method?.toUpperCase() === 'GET') {
            config.params = {...config.params, t: new Date().getTime()}
        }

        if (Object.values(config.headers).includes('application/x-www-form-urlencoded')) {
            config.data = qs.stringify(config.data)
        }

        return config
    },
    error => {
        return Promise.reject(error)
    }
)

// 响应拦截器
service.interceptors.response.use(
    response => {
        if (response.status !== 200) {
            return Promise.reject(new Error(response.statusText || 'Error'))
        }

        const res = response.data
        // 响应成功
        if (res.code === 0) {
            return res as Result<any>
        }

        // 错误提示
        ElMessage.error(res.msg)

        // 没有权限，如：未登录、登录过期等，需要跳转到登录页
        if (res.code === 401) {
            store.userStore?.setToken('')
            location.reload()
        }

        return Promise.reject(res.msg || 'Error')
    },
    error => {
        // TODO 关于用户token过期的问题，会进入这里，但是怎么去判断是token过期？这个到底是前端问题还是后端问题
        // 像是前端问题，因为后端是正常返回401？
        ElMessage.error(error.message)
        return Promise.reject(error.message)
    }
)

// 导出 axios 实例
export default service

/**
 * 构建带参数的 GET URL
 * @param baseUrl 基础 URL
 * @param params 参数列表
 */
export const buildGetUrl = (baseUrl: string, params: Map<string, any>): string => {
    // 如果 params 为空，直接返回 baseUrl
    if (params.size === 0) {
        return baseUrl;
    }

    // 将 Map 转换为 URL 查询字符串
    const queryString = Array.from(params.entries())
        .filter(([key, value]) => value !== undefined)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');

    // 如果 baseUrl 已经包含查询参数，需要使用 '&'，否则使用 '?'
    const separator = baseUrl.includes('?') ? '&' : '?';

    return `${baseUrl}${separator}${queryString}`;
}

export class Result<T> {
    constructor(public code: number, public msg: string, public data: T) {
    }
}

export class ResultError extends Error {
    code: number;
    msg: string;

    constructor(code: number = 500, msg: string = "") {
        super(msg);
        this.code = code;
        this.msg = msg;
    }

    static of(result: Result<any>) {
        return new ResultError(result.code, result.msg);
    }

    /**
     * 断言结果，如果失败则抛出异常
     * @param result
     * @param dialogErr
     */
    static assert<T>(result: Result<T> | any, dialogErr: boolean = true): Result<T> {
        if (result && result.code === 0) {
            return result;
        } else {
            if (dialogErr) {
                ElMessage.error(result.msg)
            }
            throw ResultError.of(result);
        }
    }

    toString(): string {
        return `${this.code} ${this.msg}`;
    }
}

export class AuthError extends ResultError {
    constructor(public code: number, public msg: string) {
        super(code, msg);
    }

    static of(result: Result<any>) {
        return new AuthError(result.code, result.msg);
    }
}

export class PromiseError extends Error {
    constructor(public readonly result: Result<any>) {
        super(result.toString());
    }
}
